import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import TranslateRoundedIcon from "@mui/icons-material/TranslateRounded";
import {
  ListAnatomyAnnotationsCall,
  SaveAnatomyAnnotationsBulkCall,
} from "../../../redux/api/anatomyAnnotation.api";
import {
  translateAnatomyAnnotationCall,
  translateAnatomyAnnotationsForSlideCall,
} from "../../../redux/api/pinTranslation.api";
import TranslateConfirmDialog from "../../../components/TranslateConfirmDialog";
import AITextAssistantButton from "../../../components/AITextAssistantButton";

/*
  Anatomy labeling panel
  ----------------------
  Lives next to the 3D model viewer iframe. Lets admins:
    1. Click "Add label" → enter pick mode (iframe shows a crosshair).
    2. Click on the model → iframe posts the 3D point back here.
    3. Edit name / description / color in a dialog, then save locally.
    4. Bulk save the full set to the backend with one click.

  Communication with the iframe is via postMessage:
    parent → iframe
      { target: 'model-viewer', type: 'set-annotations', list: [...] }
      { target: 'model-viewer', type: 'set-pick-mode',   on: true|false }
      { target: 'model-viewer', type: 'select',          id }
      { target: 'model-viewer', type: 'set-auto-rotate', on: true|false }
    iframe → parent
      { source: 'model-viewer', type: 'ready' }
      { source: 'model-viewer', type: 'pick',  position, normal }
      { source: 'model-viewer', type: 'select', id }
      { source: 'model-viewer', type: 'pick-miss' | 'pick-cancel' }

  We use UUID-ish local ids (`tmp_xxx`) for unsaved rows so the iframe
  can keep them anchored before they have a real DB id.

  Example anatomy annotations (suggested copy for the user):
    - "Femur" / "The thigh bone — the longest and strongest bone…"
    - "Skull" / "The bony case of the brain (cranium + facial bones)."
    - "Humerus" / "The long bone of the upper arm…"
*/

const DEFAULT_COLOR = "#ef4444";
const COLOR_PRESETS = [
  "#ef4444", // red
  "#f59e0b", // amber
  "#10b981", // emerald
  "#3b82f6", // blue
  "#a855f7", // violet
  "#ec4899", // pink
];

/*
  Quick-fill example labels by model type. These appear as chips at the
  top of the "New label" dialog so admins can author a clean reference
  in a single tap. The chips just prefill the form — admins can still
  type anything they like.
*/
const EXAMPLES_BY_TYPE = {
  anatomy: [
    {
      name: "Femur",
      description:
        "The thigh bone — the longest and strongest bone in the human body.",
    },
    {
      name: "Skull",
      description:
        "The bony case enclosing the brain. Combines the cranium and facial bones.",
    },
    {
      name: "Humerus",
      description:
        "The long bone of the upper arm extending from the shoulder to the elbow.",
    },
    {
      name: "Pelvis",
      description:
        "Bony ring (ilium, ischium, pubis + sacrum) that bears the trunk's weight.",
    },
    {
      name: "Tibia",
      description:
        "The shin bone — the larger and stronger of the two leg bones.",
    },
  ],
  // Histology 3D models — usually cells, tissues or microanatomy.
  three_d_model: [
    {
      name: "Nucleus",
      description:
        "Membrane-bound organelle that contains the cell's genetic material.",
    },
    {
      name: "Cytoplasm",
      description:
        "Jelly-like fluid inside the cell where organelles are suspended.",
    },
    {
      name: "Cell membrane",
      description:
        "Selectively permeable phospholipid bilayer that surrounds the cell.",
    },
    {
      name: "Mitochondrion",
      description:
        '"Powerhouse" of the cell — produces ATP via oxidative phosphorylation.',
    },
  ],
  // Embryology 3D models — usually developmental structures.
  emb_3d_model: [
    {
      name: "Neural tube",
      description:
        "Precursor of the central nervous system, formed by neurulation.",
    },
    {
      name: "Somite",
      description:
        "Paired blocks of paraxial mesoderm that give rise to vertebrae, ribs and dermis.",
    },
    {
      name: "Notochord",
      description:
        "Midline rod that defines the embryonic axis and induces neural plate formation.",
    },
    {
      name: "Yolk sac",
      description:
        "Extraembryonic membrane that supports early nutrition and primordial germ cells.",
    },
  ],
};

const DEFAULT_EXAMPLES = EXAMPLES_BY_TYPE.anatomy;

const examplesFor = (slideType) =>
  EXAMPLES_BY_TYPE[String(slideType || "")] || DEFAULT_EXAMPLES;

const TITLE_BY_TYPE = {
  anatomy: "Anatomy labels",
  three_d_model: "Histology labels",
  emb_3d_model: "Embryology labels",
};
const titleFor = (slideType) =>
  TITLE_BY_TYPE[String(slideType || "")] || "Model labels";

const localId = () =>
  `tmp_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

const formatPosition = (vec) => {
  if (!vec) return "";
  // <model-viewer> uses "x y z" or "x y zm" — we strip units, then re-add.
  const x = Number(vec.x ?? 0).toFixed(6);
  const y = Number(vec.y ?? 0).toFixed(6);
  const z = Number(vec.z ?? 0).toFixed(6);
  return `${x}m ${y}m ${z}m`;
};

const formatNormal = (vec) => {
  if (!vec) return "";
  const x = Number(vec.x ?? 0).toFixed(6);
  const y = Number(vec.y ?? 0).toFixed(6);
  const z = Number(vec.z ?? 0).toFixed(6);
  return `${x} ${y} ${z}`;
};

function AnatomyAnnotationsPanel({
  slideId,
  slideType,
  iframeRef,
  canEdit = false,
  onSelectionChange,
}) {
  const examples = useMemo(() => examplesFor(slideType), [slideType]);
  const panelTitle = useMemo(() => titleFor(slideType), [slideType]);

  /* Shared diagnostic logger — uses the same [PIN] prefix as the
     iframe so a single console filter shows the whole pick flow. */
  const dbg = (...args) => {
    // eslint-disable-next-line no-console
    console.log("[PIN][panel]", ...args);
  };
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const surfaceBg = isDark
    ? alpha(theme.palette.common.white, 0.04)
    : theme.palette.background.paper;
  const borderColor = isDark
    ? alpha(theme.palette.common.white, 0.12)
    : theme.palette.divider;

  const [items, setItems] = useState([]); // [{ id, name, description, position, normal, color, sort_order, _dirty?, _new? }]
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pickMode, setPickMode] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null); // { id|null, name, description, position, normal, color }
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState("");

  /* AI-translation dialog targets:
       - translateTarget       → translate ONE annotation row
       - translateAllOpen      → translate every annotation on this slide
     Both share the reusable <TranslateConfirmDialog>. */
  const [translateTarget, setTranslateTarget] = useState(null);
  const [translateAllOpen, setTranslateAllOpen] = useState(false);

  /* Count of persisted (i.e. translatable) annotations — unsaved
     `_new` rows can't be translated yet because they have no DB id. */
  const persistedCount = useMemo(
    () => items.filter((r) => !String(r.id || "").startsWith("tmp_")).length,
    [items],
  );

  // Keep an always-fresh ref so iframe message handler doesn't get
  // stale closures (it lives across many state changes).
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const pickModeRef = useRef(pickMode);
  pickModeRef.current = pickMode;

  const postToViewer = useCallback(
    (type, payload = {}) => {
      try {
        const win = iframeRef?.current?.contentWindow;
        // eslint-disable-next-line no-console
        console.log("[PIN][panel] postToViewer", type, {
          hasWin: !!win,
          payload,
        });
        if (!win) return;
        win.postMessage(
          { target: "model-viewer", type, ...payload },
          "*"
        );
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log("[PIN][panel] postToViewer:threw", String(e?.message || e));
      }
    },
    [iframeRef]
  );

  const syncToViewer = useCallback(
    (list) => {
      postToViewer("set-annotations", {
        list: list.map((it) => ({
          id: it.id,
          name: it.name,
          description: it.description || "",
          /* surface is a mesh-anchored handle returned by
             model-viewer's surfaceFromPoint — when present, the iframe
             attaches the hotspot to the exact mesh triangle and the
             position string becomes a fallback only. */
          surface: it.surface || "",
          position: it.position || "",
          normal: it.normal || "",
          color: it.color || DEFAULT_COLOR,
        })),
      });
    },
    [postToViewer]
  );

  /* Load annotations whenever slide changes. */
  useEffect(() => {
    if (!slideId) {
      setItems([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await ListAnatomyAnnotationsCall(slideId);
        if (cancelled) return;
        const list = Array.isArray(res) ? res : [];
        setItems(list);
        setDirty(false);
        // Push to viewer after the iframe has a chance to mount.
        setTimeout(() => syncToViewer(list), 50);
      } catch (e) {
        if (!cancelled) {
          setError("Failed to load annotations.");
          setItems([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slideId, syncToViewer]);

  /* Listen for messages from the iframe (pick events, selection,
     ready). We use itemsRef to avoid re-binding on every state change. */
  useEffect(() => {
    dbg("listener:mounted");
    const onMessage = (e) => {
      const data = e?.data;
      if (!data || typeof data !== "object" || data.source !== "model-viewer") {
        return;
      }
      // Diagnostic breadcrumb — easy to spot in devtools while
      // troubleshooting "the dialog never opens" / "no marker on
      // click" type reports. Same [PIN] prefix as the iframe.
      // eslint-disable-next-line no-console
      console.log("[PIN][panel] message:in", data.type, data);
      switch (data.type) {
        case "ready":
          // Re-push annotations once the viewer is ready (handles
          // iframe reload due to URL change).
          syncToViewer(itemsRef.current);
          if (pickModeRef.current) {
            postToViewer("set-pick-mode", { on: true });
          }
          break;
        case "pick": {
          const surface = data.surface || "";
          const pos = formatPosition(data.position);
          dbg("pick:received", { surface, pos, raw: data });
          // Need at least one anchor — surface (preferred) or position.
          if (!surface && !pos) {
            dbg("pick:skip — no anchor (no surface and no position)");
            return;
          }
          /* Drop the marker on the model IMMEDIATELY so the click feels
             responsive — otherwise the only visible feedback was the
             pick banner disappearing, which read as "nothing happened."
             We use a placeholder name and then open the dialog so the
             admin can rename. If they cancel, the marker stays (it can
             be edited or deleted from the list afterwards). */
          const newId = localId();
          const existing = itemsRef.current || [];
          const placeholderName = `Label ${existing.length + 1}`;
          const normal = formatNormal(data.normal);
          const newItem = {
            id: newId,
            name: placeholderName,
            description: "",
            surface,
            position: pos,
            normal,
            color: DEFAULT_COLOR,
            sort_order: existing.length,
            _new: true,
            _dirty: true,
          };
          const next = [...existing, newItem];
          dbg("pick:placing marker", { newId, name: placeholderName, nextCount: next.length });
          setItems(next);
          setDirty(true);
          setSelectedId(newId);
          syncToViewer(next);
          setPickMode(false);
          /* Pre-load the just-placed marker into the draft so the
             dialog edits the same row rather than creating a duplicate. */
          setDraft({
            id: newId,
            name: placeholderName,
            description: "",
            surface,
            position: pos,
            normal,
            color: DEFAULT_COLOR,
          });
          dbg("pick:dialog should now be open (draft set)");
          break;
        }
        case "pick-cancel":
          setPickMode(false);
          setError("");
          break;
        case "pick-miss":
          /* Click landed off the model — stay in pick mode so the user
             can try again without re-clicking "Add label". Surface a
             gentle hint so they know the click was registered. */
          setError("That click missed the model. Try again on the model surface.");
          break;
        case "pick-error":
          setError(
            (data && data.message ? `Pick error: ${data.message}` : "Pick error.") +
              " Open the iframe console for details."
          );
          setPickMode(false);
          break;
        case "select":
          setSelectedId(data.id || null);
          if (typeof onSelectionChange === "function") {
            onSelectionChange(data.id || null);
          }
          break;
        case "hotspot-moved": {
          /* The user dragged a marker to a new spot on the model.
             Update local state but do NOT push a fresh `set-annotations`
             back to the iframe — the viewer already shows the new
             position, and re-rendering would briefly recreate the
             slotted element and break the active gesture. */
          if (!data.id) return;
          setItems((prev) => {
            const next = prev.map((it) =>
              it.id === data.id
                ? {
                    ...it,
                    surface: data.surface || "",
                    position: data.position,
                    normal: data.normal || "",
                    _dirty: true,
                  }
                : it
            );
            return next;
          });
          setDirty(true);
          break;
        }
        default:
          break;
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [onSelectionChange, postToViewer, syncToViewer]);

  const startPick = useCallback(() => {
    // eslint-disable-next-line no-console
    console.log("[PIN][panel] startPick clicked");
    setPickMode(true);
    postToViewer("set-pick-mode", { on: true });
  }, [postToViewer]);

  const cancelPick = useCallback(() => {
    setPickMode(false);
    postToViewer("set-pick-mode", { on: false });
  }, [postToViewer]);

  const handleSelect = useCallback(
    (id) => {
      setSelectedId(id);
      postToViewer("select", { id });
    },
    [postToViewer]
  );

  const handleEdit = useCallback((row) => {
    setDraft({
      id: row.id,
      name: row.name,
      description: row.description || "",
      surface: row.surface || "",
      position: row.position,
      normal: row.normal || "",
      color: row.color || DEFAULT_COLOR,
    });
  }, []);

  const handleDelete = useCallback(
    (row) => {
      const next = items.filter((it) => it.id !== row.id);
      setItems(next);
      setDirty(true);
      if (selectedId === row.id) setSelectedId(null);
      syncToViewer(next);
    },
    [items, selectedId, syncToViewer]
  );

  const handleDraftSave = useCallback(() => {
    if (!draft) return;
    const name = (draft.name || "").trim();
    const position = (draft.position || "").trim();
    if (!name) {
      setError("Name is required.");
      return;
    }
    if (!position) {
      // Should not happen in normal flow (the marker is placed before
      // the dialog opens), but guard against a manual-entry edge case.
      setError("Pick a point on the model first.");
      return;
    }
    setError("");
    let next;
    if (draft.id) {
      next = items.map((it) =>
        it.id === draft.id
          ? {
              ...it,
              name,
              description: (draft.description || "").trim(),
              surface: draft.surface || it.surface || "",
              position,
              normal: draft.normal || "",
              color: draft.color || DEFAULT_COLOR,
              _dirty: true,
            }
          : it
      );
    } else {
      const newId = localId();
      next = [
        ...items,
        {
          id: newId,
          name,
          description: (draft.description || "").trim(),
          surface: draft.surface || "",
          position,
          normal: draft.normal || "",
          color: draft.color || DEFAULT_COLOR,
          sort_order: items.length,
          _new: true,
        },
      ];
      setSelectedId(newId);
    }
    setItems(next);
    setDirty(true);
    setDraft(null);
    syncToViewer(next);
  }, [draft, items, syncToViewer]);

  const handleSaveAll = useCallback(async () => {
    if (!slideId) return;
    setSaving(true);
    setError("");
    try {
      // Strip local tmp_ ids before sending so backend issues real UUIDs.
      const payload = {
        slide_id: slideId,
        items: items.map((it, idx) => ({
          id: it.id && !String(it.id).startsWith("tmp_") ? it.id : undefined,
          name: it.name,
          description: it.description || "",
          surface: it.surface || "",
          position: it.position,
          normal: it.normal || "",
          color: it.color || DEFAULT_COLOR,
          sort_order: idx,
        })),
      };
      const res = await SaveAnatomyAnnotationsBulkCall(payload);
      if (res && Array.isArray(res.items)) {
        setItems(res.items);
        setDirty(false);
        syncToViewer(res.items);
      } else if (res && res.status && res.status >= 400) {
        setError(res.message || "Failed to save annotations.");
      }
    } catch (e) {
      setError("Failed to save annotations.");
    } finally {
      setSaving(false);
    }
  }, [items, slideId, syncToViewer]);

  /* Apply one of the canonical anatomical examples to the current
     draft so the admin sees an authored, doctor-style entry without
     accidentally placing a marker at the model origin (which would
     bury it inside the mesh and feel "stuck"). */
  const applyExample = useCallback((ex) => {
    setDraft((d) => ({
      ...(d || {}),
      name: ex.name,
      description: ex.description,
    }));
  }, []);

  const countLabel = useMemo(() => {
    if (loading) return "Loading…";
    if (!items.length) return "No labels yet";
    return `${items.length} label${items.length === 1 ? "" : "s"}`;
  }, [items.length, loading]);

  if (!slideId) return null;

  return (
    <Card
      sx={{
        borderRadius: 2,
        border: `1px solid ${borderColor}`,
        backgroundColor: surfaceBg,
        backgroundImage: "none",
        boxShadow: isDark ? "none" : "0 4px 14px rgba(15, 23, 42, 0.06)",
      }}
    >
      <CardContent sx={{ pb: "16px !important" }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1.5}
          sx={{ mb: 1 }}
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {panelTitle}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Tap "Add label" then click anywhere on the model.
            </Typography>
          </Box>
          <Chip size="small" label={countLabel} />
        </Stack>

        <Divider sx={{ mb: 1.5 }} />

        {canEdit && (
          <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: "wrap", gap: 1 }}>
            <Button
              size="small"
              variant={pickMode ? "outlined" : "contained"}
              color={pickMode ? "warning" : "primary"}
              startIcon={pickMode ? <MyLocationIcon /> : <AddCircleOutlineIcon />}
              onClick={pickMode ? cancelPick : startPick}
            >
              {pickMode ? "Cancel pick" : "Add label"}
            </Button>
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={
                saving ? (
                  <CircularProgress size={14} sx={{ color: "inherit" }} />
                ) : (
                  <SaveOutlinedIcon />
                )
              }
              onClick={handleSaveAll}
              disabled={!dirty || saving}
            >
              {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
            </Button>
            <Tooltip
              title={
                persistedCount === 0
                  ? "Save your labels first, then you can translate them."
                  : dirty
                  ? "Save your pending changes before translating."
                  : `Translate every label on this 3D model into all supported languages. Existing translations are overwritten.`
              }
            >
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  color="info"
                  startIcon={<TranslateRoundedIcon />}
                  onClick={() => setTranslateAllOpen(true)}
                  disabled={persistedCount === 0 || dirty}
                >
                  Translate all
                </Button>
              </span>
            </Tooltip>
          </Stack>
        )}

        {canEdit && items.length > 0 && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 1 }}
          >
            Tip: drag any marker on the model to move it, or use the
            pencil to edit text.
          </Typography>
        )}

        {error && (
          <Typography
            variant="caption"
            color="error"
            sx={{ display: "block", mb: 1 }}
          >
            {error}
          </Typography>
        )}

        {loading ? (
          <Box sx={{ py: 3, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={20} />
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ py: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              No labels yet.
              {canEdit
                ? ' Click "Add label", then click anywhere on the model — the marker drops where you click. You can rename it in the dialog and drag it later to fine-tune.'
                : ""}
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding sx={{ maxHeight: 360, overflow: "auto" }}>
            {items.map((row) => {
              const isSelected = selectedId === row.id;
              return (
                <ListItemButton
                  key={row.id}
                  selected={isSelected}
                  onClick={() => handleSelect(isSelected ? null : row.id)}
                  sx={{
                    borderRadius: 1.5,
                    mb: 0.5,
                    px: 1,
                    py: 0.75,
                    border: `1px solid ${isSelected ? alpha(row.color || DEFAULT_COLOR, 0.55) : "transparent"}`,
                    backgroundColor: isSelected
                      ? alpha(row.color || DEFAULT_COLOR, 0.08)
                      : "transparent",
                    "&:hover": {
                      backgroundColor: alpha(row.color || DEFAULT_COLOR, 0.08),
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "999px",
                      backgroundColor: row.color || DEFAULT_COLOR,
                      mr: 1.25,
                      flexShrink: 0,
                      boxShadow: `0 0 0 2px ${alpha(row.color || DEFAULT_COLOR, 0.25)}`,
                    }}
                  />
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {row.name}
                      </Typography>
                    }
                    secondary={
                      row.description ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: "-webkit-box",
                            WebkitBoxOrient: "vertical",
                            WebkitLineClamp: 2,
                            overflow: "hidden",
                          }}
                        >
                          {row.description}
                        </Typography>
                      ) : null
                    }
                  />
                  {canEdit && (
                    <Stack direction="row" spacing={0.25}>
                      <Tooltip title="Edit label">
                        <IconButton
                          size="small"
                          onClick={(ev) => {
                            ev.stopPropagation();
                            handleEdit(row);
                          }}
                        >
                          <EditOutlinedIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={
                          String(row.id || "").startsWith("tmp_")
                            ? "Save this label before translating."
                            : row._dirty
                            ? "Save your edits before translating."
                            : "Translate this label into all supported languages (overwrites existing)."
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            disabled={
                              String(row.id || "").startsWith("tmp_") ||
                              !!row._dirty
                            }
                            onClick={(ev) => {
                              ev.stopPropagation();
                              setTranslateTarget(row);
                            }}
                          >
                            <TranslateRoundedIcon fontSize="inherit" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Delete label">
                        <IconButton
                          size="small"
                          onClick={(ev) => {
                            ev.stopPropagation();
                            handleDelete(row);
                          }}
                        >
                          <DeleteOutlineIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )}
                </ListItemButton>
              );
            })}
          </List>
        )}
      </CardContent>

      {/* Edit / create dialog. Position is fixed once picked but the
          admin can re-pick by clicking "Pick new point". */}
      <Dialog
        open={!!draft}
        onClose={() => setDraft(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          {draft?.id ? "Edit label" : "New label"}
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          <Stack spacing={1.75}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Quick examples
              </Typography>
              <Stack
                direction="row"
                spacing={0.75}
                sx={{ mt: 0.5, flexWrap: "wrap", gap: 0.75 }}
              >
                {examples.map((ex) => (
                  <Chip
                    key={ex.name}
                    size="small"
                    label={ex.name}
                    onClick={() => applyExample(ex)}
                    variant={draft?.name === ex.name ? "filled" : "outlined"}
                  />
                ))}
              </Stack>
            </Box>
            <TextField
              autoFocus
              label="Name"
              size="small"
              fullWidth
              placeholder="e.g. Femur"
              value={draft?.name || ""}
              onChange={(e) =>
                setDraft((d) => ({ ...(d || {}), name: e.target.value }))
              }
            />
            <TextField
              label="Description"
              size="small"
              fullWidth
              multiline
              minRows={3}
              maxRows={6}
              placeholder="e.g. The thigh bone — the longest and strongest bone in the human body."
              value={draft?.description || ""}
              onChange={(e) =>
                setDraft((d) => ({
                  ...(d || {}),
                  description: e.target.value,
                }))
              }
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: -0.75 }}>
              <AITextAssistantButton
                value={draft?.description || ""}
                onApply={(next) =>
                  setDraft((d) => ({
                    ...(d || {}),
                    description: next,
                  }))
                }
                context="pin"
                label="AI description"
              />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Marker color
              </Typography>
              <Stack direction="row" spacing={0.75} sx={{ mt: 0.5 }}>
                {COLOR_PRESETS.map((c) => {
                  const active = (draft?.color || DEFAULT_COLOR) === c;
                  return (
                    <Box
                      key={c}
                      onClick={() =>
                        setDraft((d) => ({ ...(d || {}), color: c }))
                      }
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: "999px",
                        backgroundColor: c,
                        cursor: "pointer",
                        border: `2px solid ${active ? theme.palette.text.primary : "transparent"}`,
                        boxShadow: `0 0 0 2px ${alpha(c, 0.18)}`,
                      }}
                    />
                  );
                })}
              </Stack>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                3D position
              </Typography>
              <TextField
                size="small"
                fullWidth
                value={draft?.position || ""}
                onChange={(e) =>
                  setDraft((d) => ({ ...(d || {}), position: e.target.value }))
                }
                helperText='Use "Pick new point" on the model to update.'
                InputProps={{
                  endAdornment: (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setDraft(null);
                        startPick();
                      }}
                    >
                      Pick new point
                    </Button>
                  ),
                }}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          {/* If this draft refers to an existing row, "Discard" removes
              the row (useful when an admin places a marker by accident
              and wants to back out). For a brand-new manual entry
              there's nothing yet to discard, so it's plain "Cancel". */}
          {draft?.id && items.some((it) => it.id === draft.id) ? (
            <Button
              color="error"
              onClick={() => {
                const id = draft.id;
                setDraft(null);
                handleDelete({ id });
              }}
            >
              Discard marker
            </Button>
          ) : (
            <Button onClick={() => setDraft(null)}>Cancel</Button>
          )}
          <Button onClick={handleDraftSave} variant="contained">
            {draft?.id ? "Save label" : "Add label"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── AI translation: ONE annotation ─────────────────────────────
          Confirms, calls the backend, and renders a per-locale chip
          summary. UPSERT semantics — re-running overwrites. */}
      <TranslateConfirmDialog
        open={!!translateTarget}
        onClose={() => setTranslateTarget(null)}
        title="Translate this label"
        description={
          translateTarget ? (
            <>
              The label{" "}
              <strong>{translateTarget.name || "(untitled)"}</strong> will be
              translated into every supported app language using AI.
            </>
          ) : null
        }
        targetLangsLabel="32 app languages: English, Portuguese (PT/BR), Spanish, Italian, German, Dutch, Chinese (Simplified/Traditional), Japanese, Korean, Arabic, French, Russian, Turkish, Polish, Indonesian, Vietnamese, Hindi, Persian, Romanian, Greek, Czech, Hungarian, Swedish, Danish, Norwegian, Finnish, Malay, Tagalog, Kazakh, Turkmen — source locale is skipped"
        itemSummaryLabel="1 label"
        confirmLabel="Translate to all languages"
        onConfirm={async ({ setStatus }) => {
          setStatus("Translating label into every locale…");
          return translateAnatomyAnnotationCall(translateTarget?.id);
        }}
        summaryRenderer={(res) => (
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              {res?.created ?? 0} created · {res?.updated ?? 0} updated.
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
              {(res?.targetLangs || []).map((code) => (
                <Chip
                  key={code}
                  size="small"
                  color="success"
                  variant="outlined"
                  label={code.toUpperCase()}
                  sx={{ mb: 0.5 }}
                />
              ))}
            </Stack>
          </Stack>
        )}
      />

      {/* ── AI translation: ALL annotations on this slide ──────────── */}
      <TranslateConfirmDialog
        open={translateAllOpen}
        onClose={() => setTranslateAllOpen(false)}
        title="Translate every label on this model"
        description={
          <>
            Every label currently saved on this 3D model will be translated
            into all supported app languages. Use this after labelling a new
            model.
          </>
        }
        targetLangsLabel="32 app languages: English, Portuguese (PT/BR), Spanish, Italian, German, Dutch, Chinese (Simplified/Traditional), Japanese, Korean, Arabic, French, Russian, Turkish, Polish, Indonesian, Vietnamese, Hindi, Persian, Romanian, Greek, Czech, Hungarian, Swedish, Danish, Norwegian, Finnish, Malay, Tagalog, Kazakh, Turkmen — source locale is skipped"
        itemSummaryLabel={`${persistedCount} label${persistedCount === 1 ? "" : "s"}`}
        confirmLabel="Translate all labels"
        onConfirm={async ({ setStatus }) => {
          setStatus(`Translating ${persistedCount} label(s)…`);
          return translateAnatomyAnnotationsForSlideCall(slideId);
        }}
        summaryRenderer={(res) => (
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              {res?.annotationsTranslated ?? 0} of {res?.annotationCount ?? 0}{" "}
              label{(res?.annotationCount ?? 0) === 1 ? "" : "s"} translated
              {res?.annotationsFailed
                ? ` · ${res.annotationsFailed} failed`
                : ""}
              .
            </Typography>
          </Stack>
        )}
      />
    </Card>
  );
}

export default AnatomyAnnotationsPanel;
